import React, { useEffect, useState } from "react";
import "./Episodes.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import BackButton from "../../GoBack/GoBack";
import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { useAuthStudent } from "../../../../context/StudentLoginContext/StudentAuthContext";
import JWPlayer from "../Players/EpisodesPlayer/JwPlayer";
import { StudentMapMenu } from "../../Map/MapMenu/MapMenu";
import { useGetQuestDetail } from "../../../../../hooks/student/quest/useGetQuestDetail";
import { useGetQuestChapters } from "../../../../../hooks/student/quest/useGetQuestChapters";
import { useAuth } from "../../../../context/LoginContext/AuthContext";

const StudentAdminEpisodes = () => {
  const { userType } = useOutletContext() || {};
  const isSchoolStudent = userType === "school-student";
  const { user } = useAuth();
  const { questId } = useParams();
  const apiUrl = process.env.REACT_APP_API_URL;
  const apiImgurl = process.env.REACT_APP_IMG_URL;
  const [lastFetchedDetails, setLastFetchedDetails] = useState(null);
  const [chapters, setChapters] = useState([]);
  const { student, fetchWithAuthStudent } = useAuthStudent();
  const [chaptersLoading, setChaptersLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const navigate = useNavigate();
  const currentUser = user?.user || student?.student;
  const isSubscribed =
    currentUser?.subscription_data?.is_subscribed || currentUser?.is_subscribed;
  const {
    questDetail,
    loading: questLoading,
    getStudentQuestDetail,
  } = useGetQuestDetail();

  useEffect(() => {
    if (isSchoolStudent) {
      getStudentQuestDetail(); // 🔥 call on mount
    }
  }, [isSchoolStudent, questId]);

  const {
    questChapters,
    loading: chapterLoading,
    getStudentQuestChapters,
  } = useGetQuestChapters();

  useEffect(() => {
    if (isSchoolStudent) {
      getStudentQuestChapters(); // 🔥 call on mount
    }
  }, [isSchoolStudent, questId]);

  useEffect(() => {
    if (!isSchoolStudent) {
      setChapters([]);
      setLastFetchedDetails(null);
      setChaptersLoading(true);
      setDetailsLoading(true);
    }
  }, [questId, isSchoolStudent]);

  useEffect(() => {
    let isMounted = true;
    const fetchChapterData = async () => {
      if (!student || !student?.token) return;

      try {
        setChaptersLoading(true);
        const response = await fetchWithAuthStudent(
          `${apiUrl}/student/quest-chapters/${questId}`,
          {
            method: "GET",
          },
        );

        if (!response.ok) throw new Error("Failed to fetch chapters");

        const data = await response.json();
        if (isMounted) {
          setChapters(data?.chapters || []);
        }
      } catch (error) {
        // Keep empty chapters on error
      } finally {
        if (isMounted) {
          setChaptersLoading(false);
        }
      }
    };

    if (!isSchoolStudent) {
      fetchChapterData();
    }

    return () => {
      isMounted = false;
    };
  }, [apiUrl, student, questId, fetchWithAuthStudent, isSchoolStudent]);

  useEffect(() => {
    let isMounted = true;
    const fetchChapterDetails = async () => {
      if (!student || !student?.token) return;

      try {
        setDetailsLoading(true);
        const response = await fetchWithAuthStudent(
          `${apiUrl}/student/quest/${questId}`,
          {
            method: "GET",
          },
        );

        if (!response.ok) throw new Error("Failed to fetch details");

        const data = await response.json();
        if (isMounted) {
          setLastFetchedDetails(data);
        }
      } catch (error) {
        // Keep null on error
      } finally {
        if (isMounted) {
          setDetailsLoading(false);
        }
      }
    };

    if (!isSchoolStudent) {
      fetchChapterDetails();
    }

    return () => {
      isMounted = false;
    };
  }, [apiUrl, student, questId, fetchWithAuthStudent, isSchoolStudent]);

  const isDetailLoading = isSchoolStudent ? questLoading : detailsLoading;
  const isChaptersLoading = isSchoolStudent ? chapterLoading : chaptersLoading;
  const displayChapters = isSchoolStudent ? questChapters : chapters;
  const loading = isDetailLoading || isChaptersLoading;

  const getVideoSource = () => {
    if (!lastFetchedDetails) return null;

    if (lastFetchedDetails?.trailerType === "jwplayer") {
      return `https://cdn.jwplayer.com/manifests/${
        lastFetchedDetails?.trailerEngJwp || lastFetchedDetails?.trailerHinJwp
      }.m3u8`;
    } else if (
      lastFetchedDetails?.trailerEng ||
      lastFetchedDetails?.trailerHin
    ) {
      return `${apiImgurl}/code/public/storage/${
        lastFetchedDetails?.trailerEng || lastFetchedDetails?.trailerHin
      }`;
    }
    return null;
  };

  const getSchlStudentVideoSource = () => {
    if (!questDetail) return null;

    if (questDetail?.trailerType === "jwplayer") {
      return `https://cdn.jwplayer.com/manifests/${
        questDetail?.trailerEngJwp || questDetail?.trailerHinJwp
      }.m3u8`;
    } else if (questDetail?.trailerEng || questDetail?.trailerHin) {
      return `${apiImgurl}/code/public/storage/${
        questDetail?.trailerEng || questDetail?.trailerHin
      }`;
    }
    return null;
  };

  const EpisodeCard = ({ title, thumbnail, chapterId, unlocked, loading }) => {
    const content = (
      <div className="QuestEpisodeCard position-relative">
        {loading ? (
          <Skeleton
            height="100%"
            width="100%"
            borderRadius={16}
            style={{
              aspectRatio: "1 / 1.1",
              borderRadius: "16px",
              display: "block",
            }}
          />
        ) : (
          <>
            <img
              src={thumbnail}
              alt={title}
              className="w-100"
              style={{
                filter: unlocked ? "none" : "grayscale(50%) brightness(50%)",
              }}
            />
            {unlocked ? (
              <img
                src="https://d2lvj32mp5x83n.cloudfront.net/static/studentadmin/quest/icon/PlayVideo.png"
                alt="play"
                className="play-icon"
              />
            ) : (
              <>
                <div className="overlay-locked" />
                <img
                  src="https://d2lvj32mp5x83n.cloudfront.net/static/studentadmin/quest/icon/LockVideo.png"
                  alt="lock"
                  className="play-icon"
                />
              </>
            )}
          </>
        )}
      </div>
    );
    const handleClick = (e) => {
      // if (accessType === "subscription" && status === "2") {
      //   e.preventDefault(); // Prevent default link behavior
      //   setShowSubscriptionModal(true); // Show modal
      // } else {
      navigate(`/student/admin/episodes/detail/${questId}/${chapterId}`);
      // }
    };

    return (
      <div className="col-lg-3 col-md-4 col-sm-12 col-12 text-center">
        <div className="card-bodyEpisode">
          {unlocked ? (
            <div onClick={handleClick} style={{ cursor: "pointer" }}>
              {content}
              <p className="EpisodeTitle mb-0">
                {loading ? <Skeleton width={80} /> : title}
              </p>
            </div>
          ) : (
            <>
              {content}
              <p className="EpisodeTitle mb-0">
                {loading ? <Skeleton width={80} /> : title}
              </p>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="StudentAdminBody StudentBodyMrgn">
      <div className="container-fluid">
        <div className="teacher-bodycontent mb-4">
          <section>
            <div className="row mb-4">
              <div className="col-md-12">
                <BackButton />
              </div>
            </div>
            <div className="row justify-content-center">
              <div className="col-md-12">
                <div className="row">
                  <div className="col-lg-7 col-md-12 mb-4 mb-lg-0">
                    {isDetailLoading ? (
                      <div className="EpisodeTrailerVideo">
                        <Skeleton
                          height="100%"
                          width="100%"
                          borderRadius={20}
                          style={{
                            aspectRatio: "16 / 9",
                            borderRadius: "20px",
                            display: "block",
                          }}
                        />
                      </div>
                    ) : userType === "school-student" ? (
                      getSchlStudentVideoSource() && (
                        <JWPlayer videoSource={getSchlStudentVideoSource()} />
                      )
                    ) : (
                      getVideoSource() && (
                        <JWPlayer videoSource={getVideoSource()} />
                      )
                    )}
                  </div>
                  <div className="col-lg-5 col-md-12 ">
                    <div className="EpisodeTrailerCard">
                      <p className="EpisodeTrailerVideo-Titl mb-2">
                        {isDetailLoading ? (
                          <Skeleton width="60%" />
                        ) : userType === "school-student" ? (
                          questDetail?.title
                        ) : (
                          lastFetchedDetails?.title
                        )}{" "}
                      </p>
                      <p className="EpisodeTrailerVideo-Count">
                        {isChaptersLoading ? (
                          <Skeleton width={80} />
                        ) : userType === "school-student" ? (
                          `${questDetail?.totalChapters || questChapters?.length || 0} Chapters`
                        ) : (
                          `${chapters?.length || 0} Chapters`
                        )}
                      </p>
                      <p className="EpisodeTrailerVideo-Para">
                        {isDetailLoading ? (
                          <Skeleton count={3} />
                        ) : userType === "school-student" ? (
                          `${questDetail?.description || ""}`
                        ) : (
                          lastFetchedDetails?.description || ""
                        )}
                      </p>
                      {isDetailLoading || isChaptersLoading ? (
                        <p className="mb-0 mt-5">
                          <Skeleton width={120} height={40} borderRadius={5} />
                        </p>
                      ) : (
                        displayChapters?.length > 0 && (
                          <p className="mb-0 mt-5">
                            <Link
                              to={`/student/admin/episodes/detail/${questId}/${displayChapters?.[0]?.id}`}
                              className="EpisodeTrailerVideo-Btn"
                            >
                              Start Now
                            </Link>
                          </p>
                        )
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-3 row">
                  {isChaptersLoading ? (
                    Array.from({ length: 4 }).map((_, index) => (
                      <EpisodeCard key={index} loading={true} />
                    ))
                  ) : (
                    <>
                      {userType === "school-student"
                        ? questChapters
                            ?.filter((chapter) => chapter.status === "1")
                            .map((chapter, index) => (
                              <EpisodeCard
                                key={chapter.id}
                                title={chapter.title}
                                thumbnail={chapter?.thumbnail_url}
                                index={index}
                                chapterId={chapter.id}
                                unlocked={chapter?.is_unlock}
                                accessType={chapter?.access_type}
                              />
                            ))
                        : chapters
                            ?.filter((chapter) => chapter.status === "1")
                            .map((chapter, index) => (
                              <EpisodeCard
                                key={chapter?.id}
                                title={chapter?.title}
                                thumbnail={chapter?.thumbnail_url}
                                index={index}
                                chapterId={chapter?.id}
                                unlocked={
                                  isSubscribed
                                    ? chapter?.is_unlock // subscribed → current flow
                                    : chapter?.can_access // normal → only can_access
                                }
                                accessType={chapter?.access_type}
                              />
                            ))}
                    </>
                  )}
                </div>
              </div>
              {/* {showSubscriptionModal && (
                <SubscriptionModal
                  handleClose={() => setShowSubscriptionModal(false)}
                  showSubscriptionModal={showSubscriptionModal}
                  navigateUrl={`/student/admin/quests`}
                />
              )} */}
            </div>
          </section>
          <div style={{}}>
            {/* Left Image */}
            <img
              src="https://d2lvj32mp5x83n.cloudfront.net/static/studentadmin/common/LeftSideDesign.png"
              alt="left"
              className="position-fixed"
              style={{
                left: 0,
                top: "10vh",
                height: "90vh",
                bottom: "auto",
                width: "auto",
                zIndex: -1, // Keeps it in the background
              }}
            />

            {/* Right Image */}
            <img
              src="https://d2lvj32mp5x83n.cloudfront.net/static/studentadmin/common/RightSideDesign.png"
              alt="right"
              className="position-fixed"
              style={{
                right: 0,
                top: "20vh",
                height: "70vh",
                width: "auto",
                zIndex: -1, // Keeps it in the background
              }}
            />

            {/* Page Content */}
          </div>
          {/* <StudentFooter /> */}
          <StudentMapMenu />
        </div>
      </div>
    </div>
  );
};

export default StudentAdminEpisodes;
